import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Server-backed typeahead for picking a customer on the ad-hoc charge form.
// As the admin types, it debounces and fetches matching users from the JSON
// endpoint (`urlValue`), renders a dropdown, and on selection stores the
// chosen user's id in a hidden field. The visible input still submits its
// raw value as `user_email`, so a manually-typed email also works (the
// controller resolves user_id first, then falls back to email). Picking a
// customer lists their companies, so staff choose which one is billed.
export default class extends Controller {
  static targets = ["input", "userId", "results", "company", "companyField"]
  static values = { url: String, minChars: { type: Number, default: 2 } }

  connect() {
    this.debounceTimer = null
    this.users = []
    this.onDocClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    clearTimeout(this.debounceTimer)
  }

  // Typing invalidates any prior selection — clear the resolved id so a stale
  // user_id can't be submitted alongside a now-different email.
  input() {
    this.userIdTarget.value = ""
    this.fillCompanies([])
    this.lookup()
  }

  lookup() {
    clearTimeout(this.debounceTimer)

    const query = this.inputTarget.value.trim()
    if (query.length < this.minCharsValue) {
      this.hide()
      return
    }

    this.debounceTimer = setTimeout(() => this.search(query), 200)
  }

  async search(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) {
        this.hide()
        return
      }
      const users = await response.json()
      this.render(users)
    } catch (_e) {
      this.hide()
    }
  }

  render(users) {
    this.users = users
    if (!users.length) {
      this.resultsTarget.innerHTML =
        `<div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">${this.noResultsText()}</div>`
      this.show()
      return
    }

    this.resultsTarget.innerHTML = users
      .map((u) => {
        const name = u.name && u.name.length ? u.name : ""
        return `
          <button type="button"
                  data-action="click->customer-search#select"
                  data-id="${u.id}"
                  data-email="${this.escape(u.email)}"
                  class="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/5">
            <span class="font-medium text-gray-800 dark:text-gray-100">${this.escape(u.email)}</span>
            ${name ? `<span class="block text-xs text-gray-500 dark:text-gray-400">${this.escape(name)}</span>` : ""}
          </button>`
      })
      .join("")
    this.show()
  }

  select(event) {
    const btn = event.currentTarget
    this.inputTarget.value = btn.dataset.email
    this.userIdTarget.value = btn.dataset.id
    const user = this.users.find((u) => String(u.id) === btn.dataset.id)
    this.fillCompanies(user ? user.companies : [])
    this.hide()
  }

  fillCompanies(companies) {
    this.companyTarget.innerHTML = ""
    companies.forEach((c) => this.companyTarget.add(new Option(c.label, c.id, c.selected, c.selected)))
    this.companyFieldTarget.classList.toggle("hidden", companies.length === 0)
  }

  show() {
    this.resultsTarget.classList.remove("hidden")
  }

  hide() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  noResultsText() {
    return this.resultsTarget.dataset.noResults || i18n.t("js.customer_search.no_results")
  }
}
